import { useEffect, useState } from "react";
import { useFilters, PERIOD_OPTIONS, COMPARISON_OPTIONS, periodToDates, type GlobalFilters } from "../lib/filters";
import { IconFilter, IconX, IconCheck, IconCalendar } from "./icons";

interface Props {
  open: boolean;
}

export function GlobalFilterBar({ open }: Props) {
  const { filters, setFilters, resetFilters } = useFilters();
  const [draft, setDraft] = useState<GlobalFilters>(filters);

  // Mantém o rascunho alinhado quando os filtros mudam por fora (ex.: "Limpar",
  // ou um link que já chega com período definido) — sem isso a barra reabre
  // mostrando uma seleção que não é a que está valendo nas telas.
  useEffect(() => { setDraft(filters); }, [filters]);

  // O período só passa a valer no "Aplicar filtros". Comparar rascunho × aplicado
  // deixa isso VISÍVEL: sem o aviso, trocar o select e fechar a barra parecia
  // "o filtro não funciona".
  const dirty = JSON.stringify(draft) !== JSON.stringify(filters);
  // Custom sem as duas datas preenchidas não pode ser aplicado.
  const incomplete = draft.period === "custom" && (!draft.customFrom || !draft.customTo);

  if (!open) return null;

  const apply = () => { if (!incomplete) setFilters(draft); };
  const reset = () => {
    resetFilters();
    setDraft({ period: "current_month", comparison: "previous_period" });
  };

  // Preview das datas calculadas para o período selecionado no rascunho
  const preview = (() => {
    if (draft.period === "custom" && (!draft.customFrom || !draft.customTo)) return null;
    try {
      const { from, to } = periodToDates(draft.period, draft.customFrom, draft.customTo);
      const fmt = (iso: string) =>
        new Date(iso).toLocaleDateString("pt-BR", {
          day: "2-digit", month: "2-digit", year: "numeric",
          timeZone: "America/Sao_Paulo",
        });
      return `${fmt(from)} → ${fmt(new Date(new Date(to).getTime() - 1).toISOString())}`;
    } catch {
      return null;
    }
  })();

  return (
    <div className="bg-white border-b border-surface-border px-6 py-4 shadow-sm">
      <div className="flex flex-wrap gap-4 items-end">

        {/* Período */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Período</label>
          <select
            value={draft.period}
            onChange={(e) => setDraft((d) => ({ ...d, period: e.target.value as GlobalFilters["period"] }))}
            className="text-sm border border-surface-border rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-tm-500/20 focus:border-tm-400 min-w-[200px]"
          >
            {PERIOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Datas customizadas */}
        {draft.period === "custom" && (
          <>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">De</label>
              <input
                type="date"
                value={draft.customFrom ?? ""}
                onChange={(e) => setDraft((d) => ({ ...d, customFrom: e.target.value }))}
                className="text-sm border border-surface-border rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-tm-500/20 focus:border-tm-400"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Até (inclusivo)</label>
              <input
                type="date"
                value={draft.customTo ?? ""}
                onChange={(e) => setDraft((d) => ({ ...d, customTo: e.target.value }))}
                className="text-sm border border-surface-border rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-tm-500/20 focus:border-tm-400"
              />
            </div>
          </>
        )}

        {/* Preview do intervalo calculado */}
        {preview && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Intervalo</label>
            <div className="flex items-center gap-1.5 text-xs text-gray-600 bg-surface-muted border border-surface-border rounded-lg px-3 py-2 h-[38px]">
              <IconCalendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              {preview}
            </div>
          </div>
        )}

        {/* Comparar com */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Comparar com</label>
          <select
            value={draft.comparison}
            onChange={(e) => setDraft((d) => ({ ...d, comparison: e.target.value as GlobalFilters["comparison"] }))}
            className="text-sm border border-surface-border rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-tm-500/20 focus:border-tm-400 min-w-[230px]"
          >
            {COMPARISON_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Ações */}
        <div className="flex items-center gap-2 ml-auto">
          {dirty && (
            <span className="text-xs text-warning-600 font-medium">
              {incomplete ? "Informe as duas datas" : "Alterações não aplicadas"}
            </span>
          )}
          <button onClick={reset} className="btn-ghost text-danger-600 hover:bg-danger-50 hover:text-danger-700">
            <IconX className="w-4 h-4" />
            Limpar
          </button>
          <button
            onClick={apply}
            disabled={incomplete}
            className={`btn-primary ${incomplete ? "opacity-50 cursor-not-allowed" : ""} ${dirty && !incomplete ? "ring-2 ring-tm-300 animate-pulse" : ""}`}
          >
            <IconCheck className="w-4 h-4" />
            Aplicar filtros
          </button>
        </div>
      </div>

      {/* Filtros ativos */}
      {(filters.clientId || filters.vehicleId || filters.driverId || filters.bankAccountId || filters.status) && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <IconFilter className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-xs text-gray-500">Filtros ativos:</span>
          {filters.clientId    && <span className="badge-blue">Cliente: {filters.clientId}</span>}
          {filters.vehicleId   && <span className="badge-blue">Veículo: {filters.vehicleId}</span>}
          {filters.driverId    && <span className="badge-blue">Motorista: {filters.driverId}</span>}
          {filters.status      && <span className="badge-blue">Status: {filters.status}</span>}
        </div>
      )}
    </div>
  );
}
